import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Search, ChevronLeft, ChevronRight, SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { StagePill } from "@/components/StagePill";
import { LeadDrawer } from "@/components/LeadDrawer";
import { CATEGORIES, SOURCES, STAGES, LEAD_TYPES, ROLE_LABEL, PRIORITIES, PRIORITY_STYLES } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const ANY = "__any__";

export default function Leads() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeLeadId, setActiveLeadId] = useState(null);

  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    stage: params.get("stage") || "",
    category: params.get("category") || "",
    source: params.get("source") || "",
    type: params.get("type") || "",
    priority: params.get("priority") || "",
  });
  const [page, setPage] = useState(1);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const fetch = async () => {
    setLoading(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries({ ...filters, page, page_size: 25 }).filter(([, v]) => v !== "" && v != null)
      );
      const { data } = await api.get("/leads", { params: cleaned });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "master_admin" || user?.role === "admin") {
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
    } else if (user) {
      setUsers([user]);
    }
  }, [user]);

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [filters, page]);

  useEffect(() => {
    const focus = params.get("focus");
    if (focus) {
      setActiveLeadId(focus);
      setDrawerOpen(true);
      params.delete("focus");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const openNew = () => { setActiveLeadId(null); setDrawerOpen(true); };
  const openLead = (id) => { setActiveLeadId(id); setDrawerOpen(true); };

  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const hasFilter = filters.q || filters.stage || filters.category || filters.source || filters.type;

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="leads-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-label">Pipeline</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.total} {data.total === 1 ? "lead" : "leads"} {hasFilter && "(filtered)"}
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-lead-btn"
          className="text-white text-sm font-medium rounded-sm py-2 px-3.5 hover:opacity-90 flex items-center gap-1.5"
          style={{ backgroundColor: "var(--theme-color)" }}
        >
          <Plus className="w-4 h-4" /> New lead
        </button>
      </header>

      {/* Filters */}
      <section className="border border-border rounded-sm p-3 bg-background flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={filters.q}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, q: e.target.value })); }}
            placeholder="Search by name, company, email, mobile…"
            data-testid="leads-search-input"
            className="w-full bg-transparent border border-border rounded-sm pl-8 pr-3 py-1.5 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none"
          />
        </div>

        <FilterSelect testId="filter-stage" value={filters.stage}
          onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, stage: v })); }}
          options={STAGES} label="Stage" />
        <FilterSelect testId="filter-category" value={filters.category}
          onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, category: v })); }}
          options={CATEGORIES} label="Category" />
        <FilterSelect testId="filter-source" value={filters.source}
          onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, source: v })); }}
          options={SOURCES} label="Source" />
        <FilterSelect testId="filter-type" value={filters.type}
          onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, type: v })); }}
          options={LEAD_TYPES} label="Type" />
        <FilterSelect testId="filter-priority" value={filters.priority}
          onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, priority: v })); }}
          options={PRIORITIES} label="Priority" />

        {hasFilter && (
          <button
            onClick={() => { setPage(1); setFilters({ q: "", stage: "", category: "", source: "", type: "", priority: "" }); }}
            data-testid="clear-filters-btn"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-2 py-1.5"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </section>

      {/* Table */}
      <section className="border border-border rounded-sm bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left">
                <Th>ID</Th>
                <Th>Name</Th>
                <Th>Company</Th>
                <Th>Contact</Th>
                <Th>Category</Th>
                <Th>Source</Th>
                <Th>Stage</Th>
                <Th>Owner</Th>
                <Th>Follow-up</Th>
                <Th>Age</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={11} className="text-center py-10 text-muted-foreground">
                  No leads. Click <strong>New lead</strong> to add one.
                </td></tr>
              )}
              {!loading && data.items.map((l) => {
                const ageDays = l.created_at ? Math.floor((Date.now() - new Date(l.created_at).getTime()) / 86400000) : 0;
                const idleDays = l.updated_at ? Math.floor((Date.now() - new Date(l.updated_at).getTime()) / 86400000) : 0;
                const stale = !["Won", "Lost"].includes(l.stage) && (
                  idleDays >= 30 ? "stale_30" : idleDays >= 15 ? "stale_15" : idleDays >= 7 ? "stale_7" : null
                );
                return (
                <tr
                  key={l.id}
                  onClick={() => openLead(l.id)}
                  className="border-t border-border surface-hover cursor-pointer"
                  data-testid={`lead-row-${l.id}`}
                >
                  <Td><span className="mono text-xs text-muted-foreground">{l.lead_no}</span></Td>
                  <Td><span className="font-medium">{l.name}</span></Td>
                  <Td>{l.company || <span className="text-muted-foreground">—</span>}</Td>
                  <Td>
                    <div className="text-xs">
                      {l.email && <div className="truncate max-w-[180px]">{l.email}</div>}
                      {l.mobile && <div className="text-muted-foreground">{l.mobile}</div>}
                      {!l.email && !l.mobile && <span className="text-muted-foreground">—</span>}
                    </div>
                  </Td>
                  <Td>{l.category}</Td>
                  <Td>{l.source}</Td>
                  <Td>
                    {l.priority ? (
                      <span data-testid={`priority-${l.id}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${PRIORITY_STYLES[l.priority] || ""}`}>
                        {l.priority}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td><StagePill stage={l.stage} /></Td>
                  <Td className="text-xs">
                    {userMap[l.owner_id]?.full_name || <span className="text-muted-foreground">Unassigned</span>}
                  </Td>
                  <Td className="text-xs mono text-muted-foreground">
                    {l.next_follow_up ? new Date(l.next_follow_up).toLocaleDateString() : "—"}
                  </Td>
                  <Td className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="mono text-muted-foreground">{ageDays}d</span>
                      {stale && (
                        <span
                          data-testid={`stale-badge-${l.id}`}
                          className={
                            "text-[10px] font-semibold px-1.5 py-0.5 rounded-sm " +
                            (stale === "stale_30" ? "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200" :
                             stale === "stale_15" ? "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200" :
                                                    "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200")
                          }
                          title={`No activity for ${idleDays} days`}
                        >
                          {stale === "stale_30" ? "30d+" : stale === "stale_15" ? "15d+" : "7d+"}
                        </span>
                      )}
                    </div>
                  </Td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border text-xs text-muted-foreground">
          <span>Page {data.page} of {pages}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page <= 1}
              data-testid="page-prev"
              className="p-1.5 rounded-sm hover:bg-secondary disabled:opacity-40"
            ><ChevronLeft className="w-4 h-4" /></button>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={data.page >= pages}
              data-testid="page-next"
              className="p-1.5 rounded-sm hover:bg-secondary disabled:opacity-40"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>

      <LeadDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        leadId={activeLeadId}
        users={users}
        onSaved={() => fetch()}
      />
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}

function FilterSelect({ value, onChange, options, label, testId }) {
  return (
    <Select
      value={value === "" ? ANY : value}
      onValueChange={(v) => onChange(v === ANY ? "" : v)}
    >
      <SelectTrigger data-testid={testId} className="w-[160px] h-9 text-sm">
        <div className="flex items-center gap-1.5">
          <SlidersHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
          <SelectValue placeholder={label} />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>All {label.toLowerCase()}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
